import React, {
  useEffect,
  useState
} from "react";

import {
  Link,
  useNavigate,
  useParams
} from "react-router-dom";

import OfferDetails from "../components/OfferDetails";

import {
  useOffers
} from "../hooks/useOffers";

import {
  OFFER_STATUS,
  OFFER_STATUS_LABELS
} from "../constants/offer.constants";

import {
  createPublicOfferLink
} from "../../../api/public-offer/publicOffer.api";

import Loader from "../../../components/common/Loader";
import ErrorMessage from "../../../components/common/ErrorMessage";
import EmptyState from "../../../components/common/EmptyState";
import Button from "../../../components/common/Button";


/*
|--------------------------------------------------------------------------
| ERROR MESSAGE HELPER
|--------------------------------------------------------------------------
*/

const getErrorMessage = (
  error
) => {

  if (!error) {
    return "";
  }


  if (
    typeof error === "string"
  ) {
    return error;
  }


  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Unable to load offer."
  );
};


/*
|--------------------------------------------------------------------------
| PAGE
|--------------------------------------------------------------------------
*/

const OfferDetailsPage = () => {

  const {
    id
  } = useParams();

  const navigate =
    useNavigate();


  /*
  |--------------------------------------------------------------------------
  | OFFER HOOK
  |--------------------------------------------------------------------------
  */

  const {
    selectedOffer,
    selectedOfferLoading,
    selectedOfferError,

    fetchOffer,
    deleteOffer,
    changeOfferStatus,

    saving,
    mutationError,

    clearErrors
  } = useOffers();


  /*
  |--------------------------------------------------------------------------
  | STATUS STATE
  |--------------------------------------------------------------------------
  */

  const [
    selectedStatus,
    setSelectedStatus
  ] = useState("");


  /*
  |--------------------------------------------------------------------------
  | PUBLIC OFFER STATE
  |--------------------------------------------------------------------------
  */

  const [
    publicOfferLoading,
    setPublicOfferLoading
  ] = useState(false);


  const [
    publicOfferError,
    setPublicOfferError
  ] = useState("");


  const [
    publicOfferUrl,
    setPublicOfferUrl
  ] = useState("");


  /*
  |--------------------------------------------------------------------------
  | LOAD OFFER
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!id) {
      return;
    }


    const loadOffer = async () => {

      try {

        clearErrors();

        await fetchOffer(id);

      } catch (err) {

        console.error(
          "Failed to load offer:",
          err
        );

      }

    };


    loadOffer();


  }, [
    id,
    fetchOffer,
    clearErrors
  ]);


  /*
  |--------------------------------------------------------------------------
  | KEEP STATUS IN SYNC
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (
      selectedOffer?.status
    ) {

      setSelectedStatus(
        selectedOffer.status
      );

    }

  }, [
    selectedOffer?.status
  ]);


  /*
  |--------------------------------------------------------------------------
  | STATUS OPTIONS
  |--------------------------------------------------------------------------
  */

  const statusOptions =
    Object.values(
      OFFER_STATUS || {}
    );


  /*
  |--------------------------------------------------------------------------
  | CREATE PUBLIC OFFER LINK
  |--------------------------------------------------------------------------
  |
  | Protected backend endpoint.
  |
  | The authenticated admin / agent / sales user
  | can generate the public link.
  |
  | POST /api/public-offers/:offerId/link
  |
  */

  const handleCreatePublicOfferLink =
    async () => {

      if (!selectedOffer) {
        return;
      }


      setPublicOfferLoading(
        true
      );

      setPublicOfferError("");


      try {

        const response =
          await createPublicOfferLink(
            selectedOffer.id
          );


        /*
        |--------------------------------------------------------------------------
        | Backend response
        |--------------------------------------------------------------------------
        |
        | {
        |   success: true,
        |   message: "...",
        |   data: {
        |     id,
        |     offerId,
        |     token,
        |     status,
        |     expiresAt,
        |     url,
        |     createdAt
        |   }
        | }
        |
        */

        const url =
          response?.data?.url;


        if (!url) {

          throw new Error(
            "Public offer link was created but the URL was not returned."
          );

        }


        setPublicOfferUrl(
          url
        );


      } catch (error) {

        console.error(
          "Failed to create public offer link:",
          error
        );


        setPublicOfferError(
          getErrorMessage(
            error
          )
        );

      } finally {

        setPublicOfferLoading(
          false
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | COPY PUBLIC OFFER LINK
  |--------------------------------------------------------------------------
  */

  const handleCopyPublicOfferLink =
    async () => {

      if (!publicOfferUrl) {
        return;
      }


      try {

        await navigator.clipboard.writeText(
          publicOfferUrl
        );


        window.alert(
          "Public offer link copied."
        );


      } catch (error) {

        console.error(
          "Failed to copy public offer link:",
          error
        );


        window.alert(
          "Unable to copy the public offer link."
        );

      }

    };


  /*
  |--------------------------------------------------------------------------
  | OPEN PUBLIC OFFER
  |--------------------------------------------------------------------------
  */

  const handleOpenPublicOffer =
    () => {

      if (!publicOfferUrl) {
        return;
      }


      window.open(
        publicOfferUrl,
        "_blank",
        "noopener,noreferrer"
      );

    };


  /*
  |--------------------------------------------------------------------------
  | STATUS CHANGE
  |--------------------------------------------------------------------------
  */

  const handleStatusChange = async (
    event
  ) => {

    const nextStatus =
      event.target.value;


    if (!nextStatus) {
      return;
    }


    if (
      !selectedOffer ||
      nextStatus ===
        selectedOffer.status
    ) {

      return;

    }


    const currentLabel =
      OFFER_STATUS_LABELS[
        selectedOffer.status
      ] ||
      selectedOffer.status;


    const nextLabel =
      OFFER_STATUS_LABELS[
        nextStatus
      ] ||
      nextStatus;


    const confirmed =
      window.confirm(
        `Change offer status from "${currentLabel}" to "${nextLabel}"?`
      );


    if (!confirmed) {

      setSelectedStatus(
        selectedOffer.status
      );

      return;

    }


    try {

      await changeOfferStatus(
        selectedOffer.id,
        nextStatus
      );


      /*
      |--------------------------------------------------------------------------
      | FETCH LATEST OFFER
      |--------------------------------------------------------------------------
      |
      | The mutation may already update selectedOffer,
      | but fetching again guarantees that the details
      | page contains the latest server state.
      |
      */

      await fetchOffer(
        selectedOffer.id
      );

    } catch (err) {

      console.error(
        "Failed to change offer status:",
        err
      );


      setSelectedStatus(
        selectedOffer.status
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | DELETE OFFER
  |--------------------------------------------------------------------------
  */

  const handleDelete = async () => {

    if (!selectedOffer) {
      return;
    }


    const confirmed =
      window.confirm(
        `Delete offer "${selectedOffer.offer_number}"? This action cannot be undone.`
      );


    if (!confirmed) {
      return;
    }


    try {

      await deleteOffer(
        selectedOffer.id
      );


      navigate(
        "/offers"
      );

    } catch (err) {

      console.error(
        "Failed to delete offer:",
        err
      );

    }

  };


  /*
  |--------------------------------------------------------------------------
  | EDIT OFFER
  |--------------------------------------------------------------------------
  */

  const handleEdit = () => {

    if (!selectedOffer) {
      return;
    }


    navigate(
      `/offers/${selectedOffer.id}/edit`
    );

  };


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (
    selectedOfferLoading
  ) {

    return <Loader />;

  }


  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  const errorMessage =
    getErrorMessage(
      selectedOfferError ||
      mutationError
    );


  if (
    selectedOfferError &&
    !selectedOffer
  ) {

    return (

      <div className="offer-details-page">

        <div className="page-header">

          <div>

            <div className="offer-details-page__breadcrumb">

              <Link
                to="/offers"
              >
                Offers
              </Link>

              <span>
                /
              </span>

              <span>
                Offer Details
              </span>

            </div>

            <h1>
              Offer Details
            </h1>

          </div>


          <Button
            type="button"
            onClick={() =>
              navigate("/offers")
            }
          >
            Back to Offers
          </Button>

        </div>


        <ErrorMessage
          message={
            errorMessage
          }
        />

      </div>

    );

  }


  /*
  |--------------------------------------------------------------------------
  | EMPTY
  |--------------------------------------------------------------------------
  */

  if (!selectedOffer) {

    return (

      <div className="offer-details-page">

        <div className="page-header">

          <div>

            <div className="offer-details-page__breadcrumb">

              <Link
                to="/offers"
              >
                Offers
              </Link>

              <span>
                /
              </span>

              <span>
                Offer Details
              </span>

            </div>

            <h1>
              Offer Details
            </h1>

          </div>


          <Button
            type="button"
            onClick={() =>
              navigate("/offers")
            }
          >
            Back to Offers
          </Button>

        </div>


        <EmptyState
          title="Offer not found"
          message="The offer you are looking for does not exist or is no longer available."
          action={
            <Link
              to="/offers"
            >
              Back to Offers
            </Link>
          }
        />

      </div>

    );

  }


  /*
  |--------------------------------------------------------------------------
  | STATUS ACTIONS
  |--------------------------------------------------------------------------
  */

  const statusActions = (

    <div className="offer-details__actions">

      {/* ---------------------------------------------------------------
          EDIT
      ---------------------------------------------------------------- */}

      <Button
        type="button"
        onClick={
          handleEdit
        }
        disabled={
          saving ||
          selectedOffer.status !==
            OFFER_STATUS.DRAFT
        }
      >
        Edit Offer
      </Button>


      {/* ---------------------------------------------------------------
          STATUS
      ---------------------------------------------------------------- */}

      <label
        className="offer-details__status-control"
      >

        <span>
          Status
        </span>


        <select
          value={
            selectedStatus ||
            selectedOffer.status
          }
          onChange={
            handleStatusChange
          }
          disabled={
            saving
          }
          aria-label="Offer status"
        >

          {statusOptions.length > 0 ? (

            statusOptions.map(
              (status) => (

                <option
                  key={status}
                  value={status}
                >
                  {
                    OFFER_STATUS_LABELS[
                      status
                    ] ||
                    status
                  }
                </option>

              )
            )

          ) : (

            <option
              value={
                selectedOffer.status
              }
            >
              {
                OFFER_STATUS_LABELS[
                  selectedOffer.status
                ] ||
                selectedOffer.status
              }
            </option>

          )}

        </select>

      </label>


      {/* ---------------------------------------------------------------
          DELETE
      ---------------------------------------------------------------- */}

      {
        selectedOffer.status ===
          OFFER_STATUS.DRAFT && (

          <Button
            type="button"
            onClick={
              handleDelete
            }
            disabled={
              saving
            }
          >
            Delete Offer
          </Button>

        )
      }


      {/* ---------------------------------------------------------------
          CREATE PUBLIC OFFER LINK
      ---------------------------------------------------------------- */}

      {
        !publicOfferUrl && (

          <Button
            type="button"
            onClick={
              handleCreatePublicOfferLink
            }
            disabled={
              saving ||
              publicOfferLoading
            }
          >
            {
              publicOfferLoading
                ? "Creating Link..."
                : "Create Public Link"
            }
          </Button>

        )
      }

    </div>

  );


  /*
  |--------------------------------------------------------------------------
  | RENDER
  |--------------------------------------------------------------------------
  */

  return (

    <div className="offer-details-page">

      {/* ================================================================
          PAGE HEADER
      ================================================================= */}

      <div className="page-header">

        <div>

          <div
            className="offer-details-page__breadcrumb"
          >

            <Link
              to="/offers"
            >
              Offers
            </Link>

            <span>
              /
            </span>

            <span>
              {
                selectedOffer.offer_number
              }
            </span>

          </div>


          <div className="offer-details-page__title-row">

            <div>

              <h1>
                Offer Details
              </h1>

              <p className="offer-details-page__subtitle">

                {
                  selectedOffer.offer_number
                }

              </p>

            </div>

          </div>

        </div>


        <Button
          type="button"
          onClick={() =>
            navigate("/offers")
          }
        >
          Back to Offers
        </Button>

      </div>


      {/* ================================================================
          ERROR / MUTATION MESSAGE
      ================================================================= */}

      {
        errorMessage && (

          <ErrorMessage
            message={
              errorMessage
            }
          />

        )
      }


      {/* ================================================================
          PUBLIC OFFER ERROR
      ================================================================= */}

      {
        publicOfferError && (

          <ErrorMessage
            message={
              publicOfferError
            }
          />

        )
      }


      {/* ================================================================
          PUBLIC OFFER LINK
      ================================================================= */}

      {
        publicOfferUrl && (

          <div className="offer-details__public-link">

            <div>

              <strong>
                Public Offer Link
              </strong>

              <p>
                Share this link with the client to view and accept the offer.
              </p>

            </div>


            <div className="offer-details__public-link-url">

              <input
                type="text"
                value={
                  publicOfferUrl
                }
                readOnly
                aria-label="Public offer link"
              />


              <Button
                type="button"
                onClick={
                  handleCopyPublicOfferLink
                }
              >
                Copy Link
              </Button>


              <Button
                type="button"
                onClick={
                  handleOpenPublicOffer
                }
              >
                Open Link
              </Button>

            </div>

          </div>

        )
      }


      {/* ================================================================
          OFFER DETAILS
      ================================================================= */}

      <OfferDetails
        offer={
          selectedOffer
        }

        onStatusChange={
          handleStatusChange
        }

        statusActions={
          statusActions
        }

        onEditItem={
          undefined
        }

        onDeleteItem={
          undefined
        }
      />

    </div>

  );

};


export default OfferDetailsPage;
